import type { FC, FocusEvent, ChangeEvent } from 'react';
// TODO: https://jira.moscow.alfaintra.net/browse/ALFABANKRU-83779
// eslint-disable-next-line package-imports/no-core-components-imports
import { MaskedInput } from '@alfalab/core-components-masked-input';
import React, { useRef, useState } from 'react';
import { StaticActions as actions } from '@alfa-bank/site-form-utils/actions';
import { isSuccess } from '@alfa-bank/site-form-helpers/meta/is-success';
import { isError } from '@alfa-bank/site-form-helpers/meta/is-error';
import { Field } from '@alfa-bank/site-form-utils/components/form-field';
import { CrossCircleMIcon } from '@alfalab/icons-glyph/CrossCircleMIcon';
import type { FieldInputProps, FieldMetaState } from 'react-final-form';

import styles from './styles.css';
import { DEFAULT_VALIDATE_FAIL } from './config';
import { validatePassport } from './helpers';

interface IPassportInputProps {
    className: string;
    onError?: () => void;
    onFocus?: () => void;
    onSuccess?: () => void;
}

// TODO Удалить `crossOrigin`, `onPointerEnterCapture` и `onPointerLeaveCapture` после перехода на React-19
// https://jira.moscow.alfaintra.net/browse/ALFABANKRU-84246
export const PassportInput: FC<IPassportInputProps> = ({
    className,
    onError,
    onFocus,
    onSuccess,
}) => {
    const isActive = useRef(false);

    const [isFieldSuccess, setFieldSuccess] = useState(false);

    const isFilled = (value: string) => {
        return value.replace(' ', '').length === 10;
    };

    const handleClick = (
        input: FieldInputProps<string, HTMLElement>,
        meta: FieldMetaState<string>
    ) => {
        input.onChange('');
        actions.getRef(meta).current.focus();
        onFocus?.();
    };

    const handleBlur = (
        event: FocusEvent<HTMLInputElement, Element>,
        input: FieldInputProps<unknown, HTMLElement>,
        meta: FieldMetaState<unknown>
    ) => {
        input.onBlur(event);
        const isValid = !validatePassport('')(event.target.value);

        isActive.current = false;

        if (isValid) {
            actions.onComplete({ input, meta });
        } else {
            onError?.();
        }
    };

    const handleChange = (
        event: ChangeEvent<HTMLInputElement>,
        input: FieldInputProps<unknown, HTMLElement>,
        meta: FieldMetaState<unknown>
    ) => {
        input.onChange(event);

        const isValid = !validatePassport('')(event.target.value);

        if (isValid) {
            actions.onComplete({ input, meta });
            if (!isFieldSuccess) {
                onSuccess?.();
                setFieldSuccess(true);
            }
        } else {
            if (isFieldSuccess) {
                setFieldSuccess(false);
            }
        }
    };

    const handleFocus = (input: FieldInputProps<unknown, HTMLElement>) => {
        input.onFocus();
        isActive.current = true;

        // При переключении с вкладки и клике по любому элементу страницы у инпута триггерится событие onFocus и onBlur
        // Сохраняем и проверяем флаг isActive чтобы не реагировать на лишний onFocus
        setTimeout(() => {
            if (isActive.current) {
                onFocus?.();
            }
        }, 100);
    };

    return (
        <Field
            name="passport"
            render={({ input, meta }) => {
                const showError = isError(meta);

                return (
                    <MaskedInput
                        ref={actions.getRef(meta)}
                        addonsClassName={showError ? styles.addonsWrapper : undefined}
                        className={className}
                        crossOrigin={undefined}
                        error={showError && meta.error}
                        label="Серия и номер паспорта"
                        mask={[/\d/, /\d/, /\d/, /\d/, ' ', /\d/, /\d/, /\d/, /\d/, /\d/, /\d/]}
                        name="passport"
                        onBlur={event => {
                            handleBlur(event, input, meta);
                        }}
                        onChange={event => {
                            handleChange(event, input, meta);
                        }}
                        onFocus={() => {
                            handleFocus(input);
                        }}
                        onPointerEnterCapture={undefined}
                        onPointerLeaveCapture={undefined}
                        pattern="[0-9]*"
                        rightAddons={
                            showError ? (
                                <div
                                    className={styles.iconErrorWrapper}
                                    onClick={() => handleClick(input, meta)}
                                    onKeyDown={() => handleClick(input, meta)}
                                    role="button"
                                    tabIndex={0}
                                >
                                    <CrossCircleMIcon color="#F15045" />
                                </div>
                            ) : undefined
                        }
                        success={isFilled(input.value) && isSuccess(meta)}
                        value={input.value}
                    />
                );
            }}
            validate={validatePassport(DEFAULT_VALIDATE_FAIL)}
        ></Field>
    );
};
